// Internal ffmpeg toolkit (CLAUDE.md invariant #7: ffmpeg is used internally — for
// `enhance`, frame extraction, and `view` — but is NOT a pluggable provider).
// ffmpeg/ffprobe are a SYSTEM PREREQUISITE: resolve an explicit override
// (OVERCAST_FFMPEG / OVERCAST_FFPROBE) or the binary on PATH. `overcast doctor`
// verifies it's installed and recent enough.

import { dirname, join, extname, basename } from "node:path";
import { existsSync, mkdirSync, mkdtempSync, copyFileSync, rmSync } from "node:fs";
import { tmpdir } from "node:os";
import { execFile } from "node:child_process";
import { createHash } from "node:crypto";
import { promisify } from "node:util";

const execFileP = promisify(execFile);

/** Recommended minimum ffmpeg/ffprobe version (major.minor). */
export const MIN_FFMPEG = "4.4";

function resolveTool(envVar: string, bin: string): string {
  // an explicit absolute-path override wins; otherwise the bare name is resolved
  // from PATH at exec time (doctor verifies presence + version).
  const override = process.env[envVar];
  if (override && existsSync(override)) return override;
  return bin;
}

export const FFMPEG_PATH = resolveTool("OVERCAST_FFMPEG", "ffmpeg");
export const FFPROBE_PATH = resolveTool("OVERCAST_FFPROBE", "ffprobe");

export interface ToolInfo {
  ok: boolean;
  path: string;
  version?: string;
  recent?: boolean; // version >= MIN_FFMPEG
  error?: string;
}

/** Probe an ffmpeg-family tool: does it run, and what version (vs MIN_FFMPEG). */
export async function probeTool(path: string): Promise<ToolInfo> {
  try {
    const { stdout } = await execFileP(path, ["-version"], { timeout: 10_000 });
    const m = stdout.match(/version\s+n?(\d+)\.(\d+)/i);
    const version = stdout.split("\n", 1)[0]?.replace(/^[a-z]+ version\s+/i, "").trim();
    let recent: boolean | undefined;
    if (m) {
      const [, maj, min] = m.map(Number);
      const [rMaj, rMin] = MIN_FFMPEG.split(".").map(Number);
      recent = maj > rMaj || (maj === rMaj && min >= rMin);
    }
    return { ok: true, path, version, recent };
  } catch (e) {
    return { ok: false, path, error: (e as Error).message };
  }
}

export type Modality = "video" | "audio" | "image" | "other";

const IMAGE_EXT = new Set([".jpg", ".jpeg", ".png", ".webp", ".gif", ".bmp", ".tiff", ".heic"]);
const AUDIO_EXT = new Set([".mp3", ".wav", ".m4a", ".aac", ".flac", ".ogg", ".opus", ".wma"]);
const VIDEO_EXT = new Set([".mp4", ".mov", ".mkv", ".webm", ".avi", ".m4v", ".flv", ".wmv"]);

/** Classify a file by extension (cheap; no probe). */
export function modalityFromExt(path: string): Modality {
  const e = extname(path).toLowerCase();
  if (IMAGE_EXT.has(e)) return "image";
  if (AUDIO_EXT.has(e)) return "audio";
  if (VIDEO_EXT.has(e)) return "video";
  return "other";
}

export interface ProbeStream {
  codec_type?: string;
  codec_name?: string;
  width?: number;
  height?: number;
  nb_frames?: string;
  avg_frame_rate?: string;
}

// codecs that decode as still images (a "video" stream that is really a picture)
const IMAGE_CODECS = new Set([
  "png", "mjpeg", "bmp", "gif", "webp", "tiff", "ppm", "pgm", "apng", "heic", "heif",
]);

export interface ProbeResult {
  durationSeconds?: number;
  hasVideo: boolean;
  hasAudio: boolean;
  width?: number;
  height?: number;
  streams: ProbeStream[];
  /** modality inferred from streams (preferred over extension) */
  modality: Modality;
}

/** ffprobe a media file into a small structured summary. */
export async function probe(path: string): Promise<ProbeResult> {
  const { stdout } = await execFileP(
    FFPROBE_PATH,
    ["-v", "error", "-print_format", "json", "-show_format", "-show_streams", path],
    { maxBuffer: 16 * 1024 * 1024 },
  );
  const parsed = JSON.parse(stdout) as {
    format?: { duration?: string };
    streams?: ProbeStream[];
  };
  const streams = parsed.streams ?? [];
  const video = streams.find((s) => s.codec_type === "video");
  const hasAudio = streams.some((s) => s.codec_type === "audio");
  const hasVideo = Boolean(video);
  const duration = parsed.format?.duration ? Number(parsed.format.duration) : undefined;

  // Detect a still image: a single video stream, no audio, decoded by an image
  // codec or with a single frame / no real duration. This works even when the
  // file has a wrong/absent extension (the extension is only a last-resort hint).
  const isImageStream =
    hasVideo &&
    !hasAudio &&
    (IMAGE_CODECS.has((video?.codec_name ?? "").toLowerCase()) ||
      video?.nb_frames === "1" ||
      ((duration === undefined || duration === 0) && video?.avg_frame_rate === "0/0"));

  let modality: Modality;
  if (isImageStream) modality = "image";
  else if (hasVideo) modality = "video";
  else if (hasAudio) modality = "audio";
  else modality = modalityFromExt(path);

  return {
    durationSeconds: duration,
    hasVideo,
    hasAudio,
    width: video?.width,
    height: video?.height,
    streams,
    modality,
  };
}

/** Ensure a directory exists and return it. */
function ensureDir(dir: string): string {
  mkdirSync(dir, { recursive: true });
  return dir;
}

/** Extract a single frame at `second` from a video to a jpg. Returns out path. */
export async function extractFrame(
  input: string,
  second: number,
  outDir: string,
): Promise<string> {
  ensureDir(outDir);
  const out = join(outDir, `${basename(input, extname(input))}_t${Math.round(second)}.jpg`);
  await execFileP(
    FFMPEG_PATH,
    ["-y", "-ss", String(second), "-i", input, "-frames:v", "1", "-q:v", "2", out],
    { maxBuffer: 16 * 1024 * 1024 },
  );
  return out;
}

/** Extract a small poster frame (≤640px wide) for a local video, so a report can
 *  show a preview WITHOUT the browser opening the video — the fast way to avoid
 *  `preload="metadata"` stalling a page full of large clips. Input-seek (`-ss`
 *  before `-i`) is instant even on a 500MB file; cached by deterministic name;
 *  returns undefined on any failure (caller falls back to no poster). */
export async function posterFrame(input: string, outDir: string, second = 0.5): Promise<string | undefined> {
  try {
    ensureDir(outDir);
    const out = join(outDir, `${basename(input, extname(input))}_poster.jpg`);
    if (existsSync(out)) return out;
    await execFileP(
      FFMPEG_PATH,
      ["-y", "-ss", String(second), "-i", input, "-frames:v", "1", "-vf", "scale='min(640,iw)':-2", "-q:v", "6", out],
      { maxBuffer: 16 * 1024 * 1024 },
    );
    return existsSync(out) ? out : undefined;
  } catch {
    return undefined;
  }
}

export interface CropBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Extract a cropped still from an image/video. For videos, `second` selects the
 * source frame; for still images it is ignored by ffmpeg. */
export async function cropStill(
  input: string,
  box: CropBox,
  out: string,
  second?: number,
): Promise<string> {
  ensureDir(dirname(out));
  const x = Math.max(0, Math.floor(box.x));
  const y = Math.max(0, Math.floor(box.y));
  const width = Math.max(1, Math.floor(box.width));
  const height = Math.max(1, Math.floor(box.height));
  const args = ["-y"];
  if (second != null && Number.isFinite(second)) args.push("-ss", String(second));
  args.push(
    "-i",
    input,
    "-frames:v",
    "1",
    "-vf",
    `crop=${width}:${height}:${x}:${y}`,
    "-q:v",
    "2",
    out,
  );
  await execFileP(FFMPEG_PATH, args, { maxBuffer: 32 * 1024 * 1024 });
  return out;
}

// ---- contact sheet (frame grid) --------------------------------------------
// Tile N timestamped frames into ONE labeled image for a single-call VLM triage
// pass (the "grid trick": T*/temporal-search + Set-of-Mark over time). Labels are
// burned per cell ONLY when this ffmpeg build has `drawtext` (needs libfreetype)
// AND a usable font is found; otherwise the sheet is unlabeled and the caller
// falls back to positional numbering. Either way the cell→timestamp map is exact
// and returned in `cells` — the burned label is a convenience, not the record.

let drawtextCache: boolean | undefined;

/** Does this ffmpeg build have the `drawtext` filter? (cached; libfreetype-gated) */
export async function hasDrawtext(): Promise<boolean> {
  if (drawtextCache !== undefined) return drawtextCache;
  try {
    const { stdout } = await execFileP(FFMPEG_PATH, ["-hide_banner", "-filters"], {
      timeout: 10_000,
      maxBuffer: 8 * 1024 * 1024,
    });
    drawtextCache = /\bdrawtext\b/.test(stdout);
  } catch {
    drawtextCache = false;
  }
  return drawtextCache;
}

// Common TTF locations across macOS + Linux; OVERCAST_GRID_FONT overrides. `.ttc`
// collections are skipped (drawtext needs a face index for those).
const FONT_CANDIDATES = [
  process.env.OVERCAST_GRID_FONT,
  "/System/Library/Fonts/Supplemental/Arial.ttf",
  "/Library/Fonts/Arial.ttf",
  "/usr/share/fonts/truetype/dejavu/DejaVuSans.ttf",
  "/usr/share/fonts/truetype/liberation/LiberationSans-Regular.ttf",
  "/usr/share/fonts/TTF/DejaVuSans.ttf",
  "/usr/share/fonts/dejavu/DejaVuSans.ttf",
];

/** First usable TTF for drawtext labels, or undefined. */
export function findGridFont(): string | undefined {
  return FONT_CANDIDATES.find((f): f is string => !!f && existsSync(f));
}

const clampInt = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, Math.round(v)));

export interface GridCell {
  /** 1-based cell number, left-to-right then top-to-bottom */
  n: number;
  /** source timestamp (seconds) this cell was sampled from, or null for a blank
   *  padding tile that fills out the last row (so every tile position maps). */
  at: number | null;
}

export interface ContactSheetResult {
  output: string;
  cells: GridCell[];
  cols: number;
  rows: number;
  cellWidth: number;
  cellHeight: number;
  /** whether cell numbers/timestamps were burned into the image */
  labeled: boolean;
}

export interface ContactSheetOpts {
  cols?: number;
  cellWidth?: number;
  outPath?: string;
  /** force labels off even when drawtext is available */
  label?: boolean;
}

/**
 * Build a labeled contact sheet from a video at the given `seconds`. Extracts one
 * uniformly-sized cell per timestamp (letterboxed to preserve aspect), burns a
 * `<n>  <t>s` label when drawtext is available, pads the final row with blank
 * cells so `tile` gets an exact grid, then tiles them into one image.
 */
export async function contactSheet(
  input: string,
  seconds: number[],
  outDir: string,
  opts: ContactSheetOpts = {},
): Promise<ContactSheetResult> {
  if (seconds.length === 0) throw new Error("contact sheet needs at least one timestamp");
  ensureDir(outDir);

  const cellWidth = clampInt(opts.cellWidth ?? 320, 120, 960);
  const p = await probe(input).catch(() => undefined);
  const aspect = p?.width && p?.height ? p.height / p.width : 9 / 16;
  let cellHeight = Math.round(cellWidth * aspect);
  if (cellHeight % 2) cellHeight += 1; // keep even for codec friendliness

  const n = seconds.length;
  const cols = clampInt(opts.cols ?? Math.ceil(Math.sqrt(n)), 1, 12);
  const rows = Math.ceil(n / cols);
  const slots = cols * rows;

  const labeled = opts.label !== false && (await hasDrawtext()) && !!findGridFont();
  const font = findGridFont();
  const fontSize = clampInt(cellWidth / 14, 14, 40);

  const base = basename(input, extname(input));
  // include a hash of the actual samples + layout so two grids of the same clip
  // with the same frame count but different windows/--at lists don't reuse (and
  // overwrite) one path — which would strand earlier records on a stale montage.
  const sig = shortHash({ seconds, cols, cellWidth, cellHeight, labeled });
  const out = opts.outPath ?? join(outDir, `${safeName(base)}_grid_${n}_${sig}.png`);
  ensureDir(dirname(out)); // a caller-supplied nested --out needs its parent created first
  const work = mkdtempSync(join(tmpdir(), "oc-grid-"));
  try {
    const scalePad =
      `scale=${cellWidth}:${cellHeight}:force_original_aspect_ratio=decrease,` +
      `pad=${cellWidth}:${cellHeight}:(ow-iw)/2:(oh-ih)/2:color=black`;
    for (let i = 0; i < n; i++) {
      const cell = join(work, `cell_${String(i + 1).padStart(3, "0")}.jpg`);
      let vf = scalePad;
      if (labeled && font) {
        // label text is number + rounded seconds only (no ':'/',' — those are
        // filtergraph separators, so the text itself needs no escaping). The font
        // path CAN contain them (a Windows `C:\...` or OVERCAST_GRID_FONT), so it
        // must be escaped for the filtergraph.
        const label = `${i + 1}  ${Math.round(seconds[i])}s`;
        vf +=
          `,drawtext=fontfile=${escFilterValue(font)}:text=${label}:x=10:y=10:` +
          `fontsize=${fontSize}:fontcolor=white:box=1:boxcolor=black@0.6:boxborderw=6`;
      }
      await execFileP(
        FFMPEG_PATH,
        ["-y", "-ss", String(seconds[i]), "-i", input, "-frames:v", "1", "-q:v", "3", "-vf", vf, cell],
        { maxBuffer: 16 * 1024 * 1024 },
      );
    }
    // pad the last row so `tile` receives an exact cols×rows sequence — the
    // partial-tile-at-EOF behavior varies across ffmpeg versions; a full grid is
    // deterministic. Fillers reuse the tile background color.
    if (n < slots) {
      const filler = join(work, "filler.jpg");
      await execFileP(
        FFMPEG_PATH,
        ["-y", "-f", "lavfi", "-i", `color=c=0x101418:s=${cellWidth}x${cellHeight}`, "-frames:v", "1", filler],
        { maxBuffer: 8 * 1024 * 1024 },
      );
      for (let i = n; i < slots; i++) {
        copyFileSync(filler, join(work, `cell_${String(i + 1).padStart(3, "0")}.jpg`));
      }
    }
    await execFileP(
      FFMPEG_PATH,
      [
        "-y", "-framerate", "1", "-i", join(work, "cell_%03d.jpg"),
        "-frames:v", "1", "-vf", `tile=${cols}x${rows}:padding=6:margin=6:color=0x101418`, out,
      ],
      { maxBuffer: 64 * 1024 * 1024 },
    );
  } finally {
    rmSync(work, { recursive: true, force: true });
  }

  // map EVERY tile position (cols×rows), not just the sampled ones — the trailing
  // blank fillers get at:null, so a cell number read off the montage always
  // resolves (to a timestamp, or to null = blank) instead of falling off the map.
  const cells: GridCell[] = Array.from({ length: slots }, (_, i) => ({
    n: i + 1,
    at: i < n ? seconds[i] : null,
  }));
  return { output: out, cells, cols, rows, cellWidth, cellHeight, labeled };
}

/** Filesystem-safe filename part. */
function safeName(s: string): string {
  return s.replace(/[^a-z0-9_.-]+/gi, "_").replace(/^_+|_+$/g, "").slice(0, 80) || "grid";
}

/** Short deterministic content hash for a collision-free default output name:
 *  same inputs → same name (idempotent), different inputs → different name (so a
 *  second run with a different window/op set can't overwrite the first's file and
 *  leave an earlier record pointing at a montage/output that no longer matches). */
function shortHash(parts: unknown): string {
  return createHash("sha1").update(JSON.stringify(parts)).digest("hex").slice(0, 10);
}

/** Escape a value (e.g. a font path) for an ffmpeg filtergraph option: backslash
 *  first, then the ':' option / ',' filter separators and the "'" quote — so a
 *  Windows `C:\Fonts\Arial.ttf` becomes `C\:\\Fonts\\Arial.ttf` instead of
 *  splitting the drawtext options. */
function escFilterValue(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/:/g, "\\:")
    .replace(/,/g, "\\,")
    .replace(/'/g, "\\'");
}

/** Render an audio spectrogram to a PNG via ffmpeg's native showspectrumpic. */
export async function spectrogram(input: string, outDir: string): Promise<string> {
  ensureDir(outDir);
  const out = join(outDir, `${basename(input, extname(input))}_spectrogram.png`);
  await execFileP(
    FFMPEG_PATH,
    ["-y", "-i", input, "-lavfi", "showspectrumpic=s=1024x512:legend=1", out],
    { maxBuffer: 16 * 1024 * 1024 },
  );
  return out;
}

/** The deterministic ffmpeg enhance ops (runtime source of truth for the type +
 *  the enhance verb's known-ops validation). Split ops (separate/segment) are NOT
 *  here — they require a bound provider (see PROVIDER_ONLY_OPS in verbs/senses). */
export const ENHANCE_OPS = ["denoise", "normalize", "voice-isolate", "upscale", "stabilize", "grayscale"] as const;
export type EnhanceOp = (typeof ENHANCE_OPS)[number];

/** Map an enhance op to an ffmpeg filter for the given modality. */
function opFilter(op: EnhanceOp, modality: Modality): { v?: string; a?: string } {
  switch (op) {
    case "denoise":
      return modality === "audio" ? { a: "afftdn" } : { v: "hqdn3d" };
    case "normalize":
      return { a: "loudnorm" };
    case "voice-isolate":
      // lightweight band-pass approximating speech isolation (deterministic)
      return { a: "highpass=f=200,lowpass=f=3000" };
    case "upscale":
      return { v: "scale=iw*2:ih*2:flags=lanczos" };
    case "stabilize":
      return { v: "deshake" };
    case "grayscale":
      return { v: "format=gray" };
    default:
      return {};
  }
}

export interface EnhanceResult {
  output: string;
  /** ops that actually contributed a filter */
  ops: EnhanceOp[];
  /** requested ops that did not apply to this modality */
  skipped: EnhanceOp[];
  modality: Modality;
}

/** Default ops per modality when none specified. */
export function defaultOps(modality: Modality): EnhanceOp[] {
  if (modality === "audio") return ["denoise", "normalize"];
  if (modality === "image") return ["denoise"];
  if (modality === "video") return ["denoise"];
  return [];
}

/**
 * Run deterministic enhance ops via ffmpeg. Combines per-modality video/audio
 * filters into a single pass. Returns the output path.
 */
export async function enhance(
  input: string,
  ops: EnhanceOp[],
  outDir: string,
  outPath?: string,
): Promise<EnhanceResult> {
  const p = await probe(input).catch(() => ({ modality: modalityFromExt(input) }) as ProbeResult);
  const modality = p.modality;
  const vFilters: string[] = [];
  const aFilters: string[] = [];
  const applied: EnhanceOp[] = [];
  const skipped: EnhanceOp[] = [];
  for (const op of ops) {
    const f = opFilter(op, modality);
    // an audio filter on an image can't apply — count it as skipped, not silent.
    const usableA = Boolean(f.a) && modality !== "image";
    if (f.v) vFilters.push(f.v);
    if (usableA) aFilters.push(f.a as string);
    if (f.v || usableA) applied.push(op);
    else skipped.push(op);
  }

  // Don't run a no-op pass that re-encodes without applying anything — that
  // would report success while changing nothing. Require at least one filter.
  if (applied.length === 0) {
    throw new Error(
      `none of the ops [${ops.join(", ")}] apply to ${modality} media`,
    );
  }

  const ext = modality === "image" ? ".png" : extname(input) || ".mp4";
  // key the default name on the applied ops so enhancing one file two different
  // ways (e.g. grayscale then denoise) doesn't overwrite the first output and
  // leave its record pointing at the wrong media — same ops still map to one file.
  const out =
    outPath ??
    join(ensureDir(outDir), `${basename(input, extname(input))}_enhanced_${shortHash(applied)}${ext}`);
  ensureDir(dirname(out)); // a caller-supplied nested --out needs its parent created too

  const args = ["-y", "-i", input];
  if (vFilters.length) args.push("-vf", vFilters.join(","));
  if (aFilters.length && modality !== "image") args.push("-af", aFilters.join(","));
  args.push(out);

  await execFileP(FFMPEG_PATH, args, { maxBuffer: 32 * 1024 * 1024 });
  return { output: out, ops: applied, modality, skipped };
}

export interface FrameRef {
  recordId: string;
  second: number;
}

/** Parse a seek/timestamp value: plain seconds ("42", "42.5") or a timecode
 *  ("1:02", "1:02:14"). Returns undefined for anything unparseable or negative —
 *  the single implementation shared by `grid` window flags and `view --at` so the
 *  two can't disagree on a malformed string. */
export function parseTimecode(s: string): number | undefined {
  const str = s.trim();
  if (!str) return undefined;
  if (str.includes(":")) {
    const parts = str.split(":").map((p) => Number(p));
    if (parts.some((p) => !Number.isFinite(p) || p < 0)) return undefined;
    return parts.reduce((acc, p) => acc * 60 + p, 0);
  }
  const n = Number(str);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

/** Parse a `frame://rec_xxx@134` reference. Returns null if not a frame ref. */
export function parseFrameRef(ref: string): FrameRef | null {
  const m = ref.match(/^frame:\/\/([^@]+)@(\d+(?:\.\d+)?)$/);
  if (!m) return null;
  return { recordId: m[1], second: Number(m[2]) };
}

export { dirname };
